import { createFileRoute } from '@tanstack/react-router';
import { Title } from '@/components/composite/Title';
import { BackButton } from '@/components/composite/buttons/BackButton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Formik, Form } from 'formik';
import { useState } from 'react';
import PreviewModal from '@/components/composite/PreviewModal';
import { useFieldTranslation } from '@/hooks/useFieldTranslation';
import { useQuestionForm } from '@/hooks/forms/useQuestionForm';
import { useQuestionFormData } from '@/hooks/forms/useQuestionFormData';
import { useQuestionFixture } from '@/hooks/forms/useQuestionFixture';
import { QuestionFieldsSection } from '@/components/forms/question/QuestionFieldsSection';
import { QuestionOptionsSection } from '@/components/forms/question/QuestionOptionsSection';
import { QuestionFormValues } from '@/utils/forms/questionTransformers';
import { QuestionCoachModal } from '@/components/coach/QuestionCoachModal';
import { QuestionCoachSection } from '@/components/forms/question/QuestionCoachSection';
import { QuestionFormSubmit } from '@/components/forms/question/QuestionFormSubmit';
import { TiebreakerOptionsClearer } from '@/components/forms/question/effects/TiebreakerOptionsClearer';
import { CustomOptionsInitializer } from '@/components/forms/question/effects/CustomOptionsInitializer';
import { OverUnderOptionsGenerator } from '@/components/forms/question/effects/OverUnderOptionsGenerator';
import { FixtureOptionsPopulator } from '@/components/forms/question/effects/FixtureOptionsPopulator';
import { PageContainer } from '@/components/layout/PageContainer';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';

export const Route = createFileRoute('/dashboard/questions/$id')({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      pickem_id: search.pickem_id as string | undefined,
    };
  },
  component: RouteComponent,
});

export function RouteComponent() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState<QuestionFormValues | null>(null);
  const [coachOpen, setCoachOpen] = useState(false);
  const { translateField, isFieldTranslating } = useFieldTranslation();

  const isNewQuestion = id === 'new';

  // Get data and derived state first
  const formData = useQuestionFormData({
    questionId: id,
    isNewQuestion,
    selectedPickemId: search.pickem_id || undefined,
  });

  const form = useQuestionForm({
    question: formData.question,
    questionId: id,
    searchPickemId: search.pickem_id,
    isNewQuestion,
  });

  // Get selected pickem ID after form initialization
  const selectedPickemId = form.initialValues.pickem_id || search.pickem_id;

  const questionLeagueId = formData.question?.league_external_id || formData.leagueId;

  // Get fixture management
  const fixture = useQuestionFixture({
    isNewQuestion,
    fixtureExternalId: form.initialValues.fixture_external_id,
    leagueId: questionLeagueId || undefined,
    initialPickemId: selectedPickemId,
    initialOptions: form.initialValues.options,
  });

  if (formData.isLoading) {
    return (
      <Card className="flex flex-col gap-4 w-full max-w-4xl mx-auto">
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <Title>Loading...</Title>
            <BackButton />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="text-muted-foreground">Loading question data...</div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <PageContainer>
      <Breadcrumbs
        overrides={{ questions: 'Questions' }}
        pathOverrides={
          search.pickem_id ? { questions: `/dashboard/pickems/${search.pickem_id}` } : undefined
        }
        searchOverrides={search.pickem_id ? { questions: { tab: 'questions' } } : undefined}
        entityName={isNewQuestion ? 'New Question' : 'Edit Question'}
      />
      <Card className="flex flex-col gap-4 w-full max-w-4xl mx-auto">
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <Title>{isNewQuestion ? 'Add new' : 'Edit'} question</Title>
            <BackButton />
          </div>
        </CardHeader>

        <CardContent>
          <QuestionCoachSection
            isNewQuestion={isNewQuestion}
            pickemId={search.pickem_id}
            onOpenCoach={() => setCoachOpen(true)}
          />
          <Formik
            initialValues={form.initialValues}
            validationSchema={form.validationSchema}
            onSubmit={values => {
              form.handleSubmit(values);
            }}
            validateOnChange={false}
            validateOnBlur={false}
            enableReinitialize
          >
            {formik => {
              return (
                <Form className="flex flex-col gap-6">
                  <TiebreakerOptionsClearer />
                  <CustomOptionsInitializer />
                  <OverUnderOptionsGenerator
                    selectedFixtureData={fixture.selectedFixtureData}
                    teamMap={fixture.teamMap}
                  />
                  <FixtureOptionsPopulator
                    selectedFixtureData={fixture.selectedFixtureData}
                    sport={formData.sport}
                    teamMap={fixture.teamMap}
                  />
                  <QuestionFieldsSection
                    formik={formik}
                    pickems={formData.pickems}
                    questionLeagueId={questionLeagueId}
                    translateField={translateField}
                    isFieldTranslating={isFieldTranslating}
                    isEditable={formData.isQuestionEditable}
                    isNewQuestion={isNewQuestion}
                    restrictedLeagueId={formData.leagueId}
                    selectedFixtureId={fixture.selectedFixtureId}
                    selectedFixtureData={fixture.selectedFixtureData}
                    teamMap={fixture.teamMap}
                    onFixtureSelect={(fixtureId, fixtureData) =>
                      fixture.handleFixtureSelect(fixtureId, fixtureData, formik)
                    }
                  />

                  {/* Leader questions (DKPLAT-354) build their option list
                      via the LeaderQuestionSection candidate modal — the
                      generic Answer Options editor would just render
                      empty default rows next to it, which is confusing.
                      Hide it for those types; for everything else the
                      editor stays as the canonical way to add/edit
                      options. */}
                  {formik.values.question_type !== 'player_leader' &&
                    formik.values.question_type !== 'team_leader' && (
                      <QuestionOptionsSection
                        formik={formik}
                        selectedTeams={
                          fixture.selectedFixtureData?.fixture
                            ? ([
                                fixture.selectedFixtureData.fixture.homeTeamId,
                                fixture.selectedFixtureData.fixture.awayTeamId,
                              ].filter(Boolean) as string[])
                            : []
                        }
                        selectedFixtureData={fixture.selectedFixtureData}
                        translateField={translateField}
                        isFieldTranslating={isFieldTranslating}
                        isEditable={formData.isQuestionEditable}
                        restrictedLeagueId={formData.leagueId}
                        pickemId={selectedPickemId}
                        leagueIp={formData.leagueIp}
                      />
                    )}

                  <QuestionFormSubmit
                    formik={formik}
                    isEditable={formData.isQuestionEditable}
                    isPending={form.isPending}
                    isError={form.isError}
                    error={form.error}
                    questionEditMessage={formData.questionEditMessage}
                    onPreview={values => {
                      setPreviewData(values);
                      setPreviewOpen(true);
                    }}
                    validationSchema={form.validationSchema}
                  />
                </Form>
              );
            }}
          </Formik>

          {previewData && (
            <PreviewModal
              open={previewOpen}
              onOpenChange={setPreviewOpen}
              questionType={previewData.question_type || 'team'}
              questionText={previewData.question_text_en}
              questionSubtext={previewData.question_subtext_en}
              questionImage={previewData.question_image_en}
              questionPoints={previewData.points}
              options={previewData.options}
              language="en"
              questionMetadata={previewData.question_metadata as any}
            />
          )}

          {/* Question Coach Modal */}
          {search.pickem_id && (
            <QuestionCoachModal
              open={coachOpen}
              onOpenChange={setCoachOpen}
              pickemId={search.pickem_id}
              restrictedLeagueId={formData.leagueId}
            />
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}
